import React, { useState, useEffect } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import { ClockIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import type { MatterDetailed } from '../../../../../types/matters';
import type { TimeEntry } from '../../../../billing/types/billing.types';
import TimeTracker from '../../TimeTracker';
import ActivityCategorySelector from '../../../../billing/components/ActivityCategorySelector';
import UtbmsCodeSelector from '../../../../billing/components/UtbmsCodeSelector';

interface TimeEntriesSectionProps {
  matter: MatterDetailed;
  timeEntries: TimeEntry[];
  loading: boolean;
  createTimeEntry: (dto: any) => Promise<TimeEntry>;
  createAndStartTimer: (dto: any) => Promise<TimeEntry>;
  activeTimer: TimeEntry | null;
  startTimer: (id: string) => Promise<TimeEntry>;
  pauseTimer: (id: string) => Promise<TimeEntry>;
  stopTimer: (id: string) => Promise<TimeEntry>;
}

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

const formatDate = (dateString: string) =>
  new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

const SkeletonRow: React.FC = () => (
  <tr>
    <td className="px-3 py-2"><div className="lex-skeleton h-4 w-20" /></td>
    <td className="px-3 py-2"><div className="lex-skeleton h-4 w-48" /></td>
    <td className="px-3 py-2 text-right"><div className="lex-skeleton h-4 w-10 ml-auto" /></td>
    <td className="px-3 py-2 text-right"><div className="lex-skeleton h-4 w-16 ml-auto" /></td>
    <td className="px-3 py-2 text-right"><div className="lex-skeleton h-4 w-16 ml-auto" /></td>
    <td className="px-3 py-2"><div className="lex-skeleton h-4 w-20" /></td>
    <td className="px-3 py-2 text-center"><div className="lex-skeleton h-5 w-16 mx-auto rounded-full" /></td>
  </tr>
);

const TimeEntriesSection: React.FC<TimeEntriesSectionProps> = ({
  matter, timeEntries, loading,
  createTimeEntry, createAndStartTimer, activeTimer, startTimer, pauseTimer, stopTimer,
}) => {
  const { keycloak } = useKeycloak();

  const [showTimeForm, setShowTimeForm] = useState(false);
  const [teDate, setTeDate] = useState(new Date().toISOString().split('T')[0]);
  const [teDuration, setTeDuration] = useState('');
  const [teRate, setTeRate] = useState('');
  const [teDescription, setTeDescription] = useState('');
  const [teActivity, setTeActivity] = useState('');
  const [teUtbmsCode, setTeUtbmsCode] = useState('');
  const [teType, setTeType] = useState<'hourly' | 'flat_rate'>('hourly');
  const [teNonBillable, setTeNonBillable] = useState(false);
  const [teNotes, setTeNotes] = useState('');
  const [teSubmitting, setTeSubmitting] = useState(false);
  const [teError, setTeError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // Auto-resolve rate from rate schedules when inline time form opens
  useEffect(() => {
    if (!showTimeForm || teRate) return;
    const params = new URLSearchParams({ matter_id: matter.id, date: teDate || new Date().toISOString().split('T')[0] });
    fetch(`/api/v1/billing/rate-schedules/resolve?${params}`, {
      headers: { Authorization: `Bearer ${keycloak?.token || ''}` },
    })
      .then((res) => res.ok ? res.json() : null)
      .then((data) => {
        if (data?.source && data.source !== 'none') {
          setTeRate(String(data.rate));
        }
      })
      .catch(() => {});
  }, [showTimeForm]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmitTime = async () => {
    if (!teDescription.trim()) { setTeError('Description is required.'); return; }
    if (!teDuration || Number(teDuration) <= 0) { setTeError('Duration is required.'); return; }
    setTeSubmitting(true);
    setTeError(null);
    try {
      await createTimeEntry({
        entry_date: teDate,
        description: teDescription,
        duration: Number(teDuration),
        rate: teRate ? Number(teRate) : undefined,
        entry_type: teType,
        is_non_billable: teNonBillable,
        activity_category: teActivity || undefined,
        utbms_code: teUtbmsCode || undefined,
        notes: teNotes || undefined,
      });
      setShowTimeForm(false);
      setTeDescription(''); setTeDuration(''); setTeRate(''); setTeActivity('');
      setTeNotes(''); setTeNonBillable(false); setTeType('hourly');
      setTeDate(new Date().toISOString().split('T')[0]);
      setSuccessMsg('Time entry saved');
      setTimeout(() => setSuccessMsg(null), 2000);
    } catch (err) {
      setTeError(err instanceof Error ? err.message : 'Failed to create time entry');
    } finally {
      setTeSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-lg font-medium text-gray-900 dark:text-white">Time Entries</h4>
        <div className="flex items-center gap-2">
          <TimeTracker
            compact
            activeTimer={activeTimer}
            onStart={createAndStartTimer}
            onPause={pauseTimer}
            onResume={startTimer}
            onStop={stopTimer}
            defaultDescription={`${matter.displayNumber || matter.title} — time`}
          />
          <button
            onClick={() => setShowTimeForm(!showTimeForm)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <ClockIcon className="h-4 w-4" />
            Log Time
          </button>
        </div>
      </div>

      {/* Success toast */}
      {successMsg && (
        <div className="lex-success-toast flex items-center gap-2 px-4 py-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
          <CheckCircleIcon className="h-4 w-4 text-green-600 dark:text-green-400" />
          <span className="text-sm text-green-700 dark:text-green-300">{successMsg}</span>
        </div>
      )}

      {/* Inline Log Time Form — animated expand */}
      {showTimeForm && (
        <div className="lex-form-panel bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800 rounded-lg p-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Date</label>
              <input type="date" value={teDate} onChange={(e) => setTeDate(e.target.value)}
                className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Duration (hrs)</label>
              <input type="number" step="0.1" min="0" value={teDuration} onChange={(e) => setTeDuration(e.target.value)} placeholder="e.g. 1.5"
                className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Rate ($/hr)</label>
              <input type="number" step="0.01" min="0" value={teRate} onChange={(e) => setTeRate(e.target.value)} placeholder="e.g. 350"
                className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Description *</label>
            <textarea value={teDescription} onChange={(e) => setTeDescription(e.target.value)} rows={2} placeholder="Describe the work performed..."
              className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white resize-none" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <ActivityCategorySelector
                value={teActivity}
                onChange={setTeActivity}
                onUtbmsCodeSuggested={(code) => { if (!teUtbmsCode) setTeUtbmsCode(code); }}
                label="Activity Category"
                placeholder="e.g. Research"
              />
            </div>
            <div>
              <UtbmsCodeSelector
                value={teUtbmsCode}
                onChange={setTeUtbmsCode}
                label="UTBMS Code"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Entry Type</label>
              <select value={teType} onChange={(e) => setTeType(e.target.value as 'hourly' | 'flat_rate')}
                className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white">
                <option value="hourly">Hourly</option>
                <option value="flat_rate">Flat Rate</option>
              </select>
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 py-1.5 cursor-pointer">
                <input type="checkbox" checked={teNonBillable} onChange={(e) => setTeNonBillable(e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                <span className="text-sm text-gray-700 dark:text-gray-300">Non-billable</span>
              </label>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Notes</label>
            <input type="text" value={teNotes} onChange={(e) => setTeNotes(e.target.value)} placeholder="Optional notes..."
              className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white" />
          </div>
          {teError && <p className="text-xs text-red-500">{teError}</p>}
          <div className="flex justify-end gap-2">
            <button onClick={() => { setShowTimeForm(false); setTeError(null); }}
              className="px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800">
              Cancel
            </button>
            <button onClick={handleSubmitTime} disabled={teSubmitting}
              className="px-4 py-1.5 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
              {teSubmitting ? 'Saving...' : 'Save Time Entry'}
            </button>
          </div>
        </div>
      )}

      {/* Time Entries Table — skeleton loader + staggered row entrance */}
      {loading ? (
        <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300 font-medium">Date</th>
                <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300 font-medium">Description</th>
                <th className="px-3 py-2 text-right text-gray-600 dark:text-gray-300 font-medium">Hours</th>
                <th className="px-3 py-2 text-right text-gray-600 dark:text-gray-300 font-medium">Rate</th>
                <th className="px-3 py-2 text-right text-gray-600 dark:text-gray-300 font-medium">Amount</th>
                <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300 font-medium">Activity</th>
                <th className="px-3 py-2 text-center text-gray-600 dark:text-gray-300 font-medium">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {[...Array(5)].map((_, i) => <SkeletonRow key={i} />)}
            </tbody>
          </table>
        </div>
      ) : timeEntries.length === 0 ? (
        <div className="text-center py-6 text-gray-500 dark:text-gray-400 text-sm">
          No time entries for this engagement yet.
        </div>
      ) : (
        <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300 font-medium">Date</th>
                <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300 font-medium">Description</th>
                <th className="px-3 py-2 text-right text-gray-600 dark:text-gray-300 font-medium">Hours</th>
                <th className="px-3 py-2 text-right text-gray-600 dark:text-gray-300 font-medium">Rate</th>
                <th className="px-3 py-2 text-right text-gray-600 dark:text-gray-300 font-medium">Amount</th>
                <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300 font-medium">Activity</th>
                <th className="px-3 py-2 text-center text-gray-600 dark:text-gray-300 font-medium">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {timeEntries.map((te, i) => (
                <tr key={te.time_entry_id} className="lex-list-row hover:bg-gray-50 dark:hover:bg-gray-800/50" style={{ '--row-index': i } as React.CSSProperties}>
                  <td className="px-3 py-2 text-gray-900 dark:text-white whitespace-nowrap">{formatDate(te.entry_date)}</td>
                  <td className="px-3 py-2 text-gray-900 dark:text-white max-w-xs truncate">{te.description}</td>
                  <td className="px-3 py-2 text-right text-gray-900 dark:text-white tabular-nums">{Number(te.duration).toFixed(1)}</td>
                  <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300 tabular-nums">
                    {te.rate != null ? formatCurrency(Number(te.rate)) : '-'}
                  </td>
                  <td className="px-3 py-2 text-right text-gray-900 dark:text-white font-medium tabular-nums">{formatCurrency(Number(te.amount))}</td>
                  <td className="px-3 py-2 text-gray-700 dark:text-gray-300 whitespace-nowrap">{te.activity_category || '-'}</td>
                  <td className="px-3 py-2 text-center">
                    {te.is_billed ? (
                      <span className="inline-flex px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300">Billed</span>
                    ) : te.is_non_billable ? (
                      <span className="inline-flex px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400">Non-billable</span>
                    ) : (
                      <span className="inline-flex px-2 py-0.5 text-xs rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/20 dark:text-amber-300">Unbilled</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default TimeEntriesSection;
